<template>
  <layout-dashboard>
    <h5 class="font-weight-bold m-0">
      User Management
    </h5>
    <small class="text-info">
      Manage User
    </small>

    <filters
      :filters="filters"
      @apply="applyFilters"
      @reset="resetFilters"
    />

    <div class="card mt-4">
      <div class="card-body">
        <div class="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between">
          <div class="mb-2">
            <router-link
              v-if="permission.permissions.includes('add')"
              tag="button"
              class="btn btn-info btn-sm px-3"
              to="registration"
            >
              {{ $t('user-management.add-user') }}
            </router-link>
          </div>
          <div class="d-flex align-items-center mb-2">
            <small>{{ $t('pagination.show') }}</small>
            <select
              v-model="pagination.PageSize"
              class="form-control form-control-sm mx-2"
            >
              <option
                v-for="(size, s) in pageSizes"
                :key="s"
                :value="size"
              >
                {{ size }}
              </option>
            </select>
            <small>{{ $t('pagination.entries') }}</small>
          </div>
          <div class="mb-2">
            <div class="input-group">
              <div class="input-group-prepend">
                <div class="input-group-text bg-white text-info pt-1">
                  <i
                    class="fa fa-search"
                    aria-hidden="true"
                  />
                </div>
              </div>
              <input
                v-model="search"
                type="text"
                class="form-control form-control-sm"
                :placeholder="$t('user-management.search')"
                @keyup.enter="fetch"
              >
            </div>
          </div>
        </div>

        <data-table
          class="my-3"
          :fields="fields"
          :data="users"
          :row-class="getRowClass"
          :data-manager="dataManager"
        >
          <template #action="{ rowData }">
            <div class="d-flex justify-content-between">
              <a
                v-if="permission.permissions.includes('read')"
                class="text-info mr-2"
                :title="rowData.id"
                @click="showModal('view', rowData)"
              >
                <i class="fa fa-eye fa-lg" />
              </a>
              <a
                v-if="permission.permissions.includes('edit')"
                class="text-info mr-2"
                :title="rowData.id"
                @click="showModal('edit', rowData)"
              >
                <i class="fa fa-edit fa-lg" />
              </a>
              <a
                v-if="permission.permissions.includes('delete')"
                class="text-danger mr-2"
                :title="rowData.id"
                @click="showModal('del', rowData)"
              >
                <i class="fa fa-trash fa-lg" />
              </a>
            </div>
          </template>

          <template #avatar="{ rowData }">
            <img
              class="avatar"
              :src="getAvatar(rowData.avatar)"
              :alt="rowData.fullname"
            >
          </template>

          <template #isActive="{ rowData }">
            <toggle-button
              v-if="!rowData.isActiveDirectory"
              :value="rowData.isActive"
              color="#18B2BD"
              sync
              @input="confirmStatus(rowData, rowData.isActive)"
            />
          </template>

          <template #comorbid="{ rowData }">
            <span v-if="rowData.comorbid">{{ $t('user-management.comorbid') }}</span>
            <span v-else>{{ $t('user-management.non-comorbid') }}</span>
          </template>

          <template #isActiveDirectory="{ rowData }">
            <div
              v-if="rowData.isActiveDirectory"
              :class="{
                'text-success': !isNotHrisLinked(rowData),
                'text-danger': isNotHrisLinked(rowData)
              }"
            >
              {{ $t('user-management.hris-linkage') }}
              <i
                class="fa"
                :class="{
                  'fa-check': !isNotHrisLinked(rowData),
                  'fa-times': isNotHrisLinked(rowData)
                }"
              />
            </div>
          </template>

          <template #email="{ rowData }">
            <i
              class="fa"
              :class="{ 'fa-check text-success': rowData.isVerified, 'fa-times text-danger': !rowData.isVerified }"
            />
            {{ rowData.email }}
          </template>
        </data-table>

        <div class="d-flex flex-wrap justify-content-center justify-content-sm-between">
          <div class="mb-2">
            <pagination-info
              :page="users.currentPage"
              :page-size="users.pageSize"
              :shown="users.data.length"
              :total="users.totalData"
            />
          </div>

          <pagination
            ref="pagination"
            class="mb-2"
            @change="onPageChange"
          />
        </div>
      </div>
    </div>

    <modal-update-status
      v-bind="status"
      @reset="resetStatus"
      @update="updateStatus"
    />

    <modal-edit-user
      v-if="edit.modal"
      v-bind="edit"
      @close="closeModal"
    />

    <modal-delete-user
      v-bind="del"
      @reset="closeModal"
    />

    <modal-view-detail
      v-if="view.modal"
      v-bind="view"
      @close="handlingCloseView"
    />

    <modal-unsuccessful
      v-show="unsuccessful.modal"
      :text-modal="unsuccessful.textModal"
      @closeModal="closeModal"
    />
  </layout-dashboard>
</template>

<script>
import { ToggleButton } from 'vue-js-toggle-button'
import { PAGE_SIZES } from '@/fixtures'
import api from '@/utils/Api'
import LayoutDashboard from '@/components/LayoutDashboard'
import PaginationInfo from '@/components/PaginationInfo'
import Pagination from '@/components/Pagination'
import DataTable from '@/components/DataTable'
import fields from './UserManagement/fields'
import Filters from './UserManagement/Filters'
import ModalUpdateStatus from './UserManagement/ModalUpdateStatus'
import ModalEditUser from './UserManagement/ModalEditUser'
import ModalDeleteUser from './UserManagement/ModalDeleteUser'
import ModalViewDetail from './UserManagement/ModalViewDetail'
import ModalUnsuccessful from '@/components/modal/ModalUnsuccessful'

const INITIAL_FILTERS = {
  EmployeeType: [],
  IsHris: false,
  status: [],
  companyId: [],
  workLocationId: [],
  contractorId: []
}

const SORT_DIRECTIONS = {
  asc: '',
  desc: '-'
}

export default {
  components: {
    ToggleButton,
    LayoutDashboard,
    PaginationInfo,
    Pagination,
    DataTable,
    Filters,
    ModalEditUser,
    ModalUpdateStatus,
    ModalDeleteUser,
    ModalViewDetail,
    ModalUnsuccessful
  },

  data () {
    return {
      profile: JSON.parse(localStorage.getItem('profile')),
      pageSizes: PAGE_SIZES,
      pagination: {
        Page: 1,
        PageSize: 10
      },
      filters: JSON.parse(JSON.stringify(INITIAL_FILTERS)),
      sorts: '-HrisStatus',
      search: '',
      fields: fields(this.$i18n),
      users: {
        data: []
      },
      status: {
        modal: false,
        user: {}
      },
      del: {
        modal: false,
        user: null
      },
      edit: {
        modal: false,
        user: null
      },
      view: {
        modal: false,
        user: null
      },
      unsuccessful: {
        modal: false,
        user: null,
        textModal: ''
      },
      permission: []
    }
  },

  watch: {
    search (val) {
      if (!val) this.fetch()
    },
    'pagination.PageSize' () {
      this.pagination.Page = 1
      this.fetch()
    }
  },

  created () {
    this.getPermission()
    this.fetch()
  },

  methods: {
    getPermission () {
      const dataLoc = JSON.parse(localStorage.getItem('profile'))
      const index = dataLoc.role.permissions.findIndex(element => element.category === 'user')
      this.permission = dataLoc.role.permissions[index]
    },
    async fetch () {
      const { data } = await api.users.list({
        ...this.pagination,
        Filters: this.buildFilters(this.filters),
        Sorts: this.sorts,
        Search: this.search
      })
      this.users = data
      this.$refs.pagination.setPaginationData({
        total: data.totalData,
        per_page: data.pageSize,
        current_page: data.currentPage,
        last_page: data.totalPage
      })
    },
    async updateStatus () {
      const { id, isActive } = this.status.user
      await api.users.updateStatus(id, {
        isActive: !isActive,
        language: this.$i18n.locale.toUpperCase()
      })
      this.users.data.find((user) => user.id === id).isActive = !isActive
      this.resetStatus()
    },
    dataManager (sorts) {
      if (sorts.length) {
        const { sortField, direction } = sorts[0]
        this.sorts = `${SORT_DIRECTIONS[direction]}${sortField}`
        this.fetch()
      }
    },
    onPageChange (page) {
      this.pagination.Page = page
      this.fetch()
    },
    confirmStatus (user) {
      this.status.modal = true
      this.status.user = user
    },
    resetStatus () {
      this.status = {
        modal: false,
        user: {}
      }
    },
    showModal (mode, user) {
      this[mode].modal = true
      this[mode].user = user
    },
    closeModal (mode) {
      this[mode].modal = false
      this[mode].user = null
      this.fetch()
    },
    handlingCloseView (mode, isUpdate, user) {
      if (isUpdate) {
        this.showModal('edit', user)
      }
      this.closeModal(mode)
    },
    applyFilters () {
      this.fetch()
    },
    resetFilters () {
      this.filters = JSON.parse(JSON.stringify(INITIAL_FILTERS))
      this.fetch()
    },
    getRowClass (row) {
      return this.isNotHrisLinked(row) && 'bg-linkage'
    },
    buildFilters: filters => Object.keys(filters)
      .filter(key => Array.isArray(filters[key])
        ? filters[key].length
        : true
      )
      .map(key => `${key}==${Array.isArray(filters[key]) && filters[key].length
        ? filters[key].join('|')
        : filters[key]}`
      )
      .join(','),
    isNotHrisLinked: row => row.isActiveDirectory && !row.hrisEmpId,
    getAvatar: avatar => avatar && avatar.startsWith('http')
      ? avatar
      : require('@/assets/icon/guest.png')
  }
}
</script>

<style lang="scss" scoped>
.card {
  border: 0;
  border-radius: 18px;
}

.avatar {
  border-radius: 50%;
  height: 48px;
  object-fit: cover;
  width: 48px;
}

/deep/ .bg-linkage {
  background: #FCDDDC !important;
}
</style>
